import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useCall } from "@/context/CallContext";
import { useAuth } from "@/context/AuthContext";
import { isPaid } from "@/lib/membership";
import { MessageCircle, Video, BadgeCheck, MapPin } from "lucide-react";
import { VerifiedBadge } from "@/components/VerifiedBadge";

const FALLBACK = "https://images.unsplash.com/photo-1637059880830-59a90102de77";

export default function Matches() {
  const [matches, setMatches] = useState(null);
  const [sort, setSort] = useState("newest");
  const { startCall } = useCall();
  const { user } = useAuth();
  const paid = isPaid(user);
  const nav = useNavigate();

  useEffect(() => { api.get(`/matches?sort=${sort}`).then(({ data }) => setMatches(data)); }, [sort]);

  if (!matches) return <div className="pt-32 text-center text-text-muted">Loading…</div>;

  const SORTS = [["newest", "Newest"], ["active", "Recently active"], ["distance", "Nearest"]];

  return (
    <div className="pt-24 pb-24 max-w-7xl mx-auto px-6">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Mutual</p>
        <h1 className="font-display text-6xl font-light mb-4">Your matches</h1>
        <p className="text-text-muted mb-8">{matches.length} mutual connection{matches.length === 1 ? "" : "s"} — chat or go live in one tap.</p>
        <div className="flex flex-wrap gap-5 mb-12">
          {SORTS.map(([val, lbl]) => (
            <button key={val} data-testid={`sort-${val}`} onClick={() => setSort(val)}
              className={`text-xs uppercase tracking-[0.15em] pb-1 border-b transition-colors ${sort === val ? "text-text-main border-wine" : "text-text-muted border-transparent hover:text-text-main"}`}>
              {lbl}
            </button>
          ))}
        </div>
      </motion.div>

      {matches.length === 0 ? (
        <p data-testid="no-matches" className="text-text-muted font-display text-2xl italic">No matches yet — admire someone who admires you back.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {matches.map((m, i) => {
            const u = m.user;
            return (
              <motion.div key={u.id} data-testid={`match-${u.id}`}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                className="rounded-2xl border border-white/8 bg-surface overflow-hidden">
                <button onClick={() => nav(`/profile/${u.id}`)} className="relative block w-full aspect-[4/3] overflow-hidden">
                  <img src={u.photo_url || FALLBACK} alt={u.display_name} className={`w-full h-full object-cover ${(u.discreet_mode || !paid) ? "blur-2xl scale-110" : ""}`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent" />
                </button>
                <div className="p-5">
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-2xl leading-none">{u.display_name}<span className="text-text-muted text-lg">, {u.age}</span></h3>
                    {u.verified && <BadgeCheck size={18} className="text-emerald-400" />}
                    <VerifiedBadge user={u} size="xs" testId={`match-id-verified-${u.id}`} />
                  </div>
                  {u.location && <p className="flex items-center gap-1 text-xs text-text-muted mt-2"><MapPin size={12} /> {u.location}{m.distance_km != null ? ` · ${m.distance_km} km away` : ""}</p>}
                  <div className="flex gap-3 mt-5">
                    <button data-testid={`match-chat-${u.id}`} onClick={() => nav(`/messages/${u.id}`)}
                      className="flex-1 flex items-center justify-center gap-2 py-3 bg-wine hover:bg-wine-hover transition-colors rounded-full text-xs uppercase tracking-[0.15em]">
                      <MessageCircle size={15} /> Chat
                    </button>
                    <button data-testid={`match-live-${u.id}`} onClick={() => startCall(u)}
                      className="flex-1 flex items-center justify-center gap-2 py-3 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 transition-colors rounded-full text-xs uppercase tracking-[0.15em]">
                      <Video size={15} /> Go live
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
